import type { ModuleEntry } from './types';

/**
 * Module-by-module reference appendix.
 *
 * WHAT THIS IS FOR, and how it differs from the role tracks: a track answers
 * "what do I do?", in order, for one person. This answers "what is this screen
 * and am I in the right place?" for someone who has landed somewhere and needs
 * orienting. Same document, different question.
 *
 * ⚠️ DELIBERATELY THIN ON PROCEDURE. The concern that deferred this
 * (2026-07-28) was that a module reference is the most drift-prone and most
 * expensive part of a guide to keep current. It drifts because it duplicates:
 * restate "how to confirm a concrete order" here and there are now two copies
 * to update, which in practice means one gets updated.
 *
 * So each entry carries what is genuinely REFERENCE — where the screen lives,
 * what it is for, who can open it, and the concepts unique to it (statuses,
 * lifecycles, what a column means). Procedures stay in the tracks, where they
 * are written once for the person who performs them.
 *
 * Access rules mirror the app's real gating. The project workspace itself only
 * hides three tabs outright (Commitments, Safety, Reports — from field-leader-
 * only users, see ProjectWorkspace); everything else gates inside the tab, so
 * most entries here carry the permission the tab's own content requires.
 */
export const MODULES: ModuleEntry[] = [
  // ── Your own day ──────────────────────────────────────────────────────────
  {
    id: 'mod-today',
    name: 'Today',
    location: 'Top navigation ▸ Today',
    whatItsFor:
      'The jobs you are named on, gathered into one screen: what is starting in the next three weeks and how ready it is, what needs doing, what has gone quiet, and what has changed lately. It is a reading screen — nothing is edited here, and any row or chip naming a single job opens it. The counts along the top are figures rather than links, and a row covering several jobs has none to open.',
    // ⚠️ BOTH rules, and the second is not redundant: leading a crew on a job
    // IS an assignment, so a field-leader-only user passes the first one while
    // the navigation gives them Projects and nothing else. (Codex, #512.)
    access: { requiresActiveAssignment: true, hideFromFieldLeaderOnly: true },
    topics: [
      {
        id: 'mod-today-scope',
        title: 'Why you may not have this tab',
        summary:
          'Today appears when you are named on at least one live job — as PM, second PM, estimator, project engineer or safety manager, or as a field leader if you also hold office access. A field leader with no office access keeps the single Projects tab instead. No live jobs, no tab, and that includes admins: it is your own work, not an oversight screen.',
      },
      {
        id: 'mod-today-lanes',
        title: 'The three lanes sort themselves',
        summary:
          'Due today, This week and Watch are worked out from each row\u2019s date rather than set by hand, so a row moves between them on its own as the date approaches. One deliberate exception: a job with more than half its critical readiness criteria still unset is held at no lower than This week rather than sinking into Watch, though it still moves up to Due today when its own date warrants \u2014 the rule sets a floor, not a destination. That check only reaches a limited distance ahead, and how far is an admin setting, so a job further out than the window can show a poor readiness figure without raising a row yet. A change order that has sat in its stage for a week gets the same treatment from the other direction: its row has no date at all, because a reminder is not a deadline, and it is held in This week rather than showing as overdue. The number of days is a configuration value, so it can be changed without a release.',
      },
      {
        id: 'mod-today-notbuilt',
        title: '“Not built yet” is not “nothing to do”',
        summary:
          'The safety card says outright that it is not built yet rather than showing an empty list. The two recurring deadline tiles are not drawn at all, for the same reason \u2014 nothing feeds them yet. Neither is a zero, and that is the point: a number on this page always means the query ran and counted.',
      },
      {
        id: 'mod-today-readiness',
        title: 'The readiness figure is the checklist, counted',
        summary:
          'Each job in the three-week strip shows how many readiness criteria are met out of the number that apply to it. The denominator varies by job on purpose — one item drops out once a job has started — so two jobs can legitimately show different totals.',
      },
    ],
  },
  // ── The job workspace ─────────────────────────────────────────────────────
  {
    id: 'mod-projects',
    name: 'Projects',
    location: 'Top navigation ▸ Projects',
    whatItsFor:
      'The master list of jobs and the way into every one of them. What you see here depends on your access: office users see the jobs they are assigned to or, with company-wide access, everything; field leaders see only the jobs they lead.',
    topics: [
      {
        id: 'mod-projects-scope',
        title: 'Why two people see different lists',
        summary:
          'The list is scoped, not filtered. A job missing from your list means you are not on it, not that it does not exist.',
        watchOut: [
          'If a job you are working on is not listed, ask the PM to add you — assignment is what grants visibility.',
        ],
        deepLink: '/projects',
      },
      {
        // #541, answering #460 and #482. Reference, because it is "what a column
        // means" — the appendix's own definition of what belongs here.
        //
        // ⚠️ NOT "always the planned start". `effectiveStart` in ProjectsPage
        // puts a remobilization's start first, and the cell marks it ↺, so the
        // column holds the latest mobilization's start on a remobilized job. The
        // sort and the start-date filter follow the same date the cell shows.
        //
        // ⚠️ And not "an actual start is never on this list" either. A fab row
        // takes each phase's start as `plannedStart ?? originalStart ??
        // actualStart` (listPhaseSpans), so a phase with only an actual start
        // recorded shows that — the alternative was "—" on a row holding a date.
        // Non-fab rows read current_start_date, which is planned-only. The first
        // wording denied the fallback outright, which invites a reader to take
        // that date for a plan (Codex, #545).
        //
        // ⚠️ The ORIGINAL start is in that chain too, ahead of the actual: a fab
        // phase with no planned start shows its baseline, and the sort and filter
        // use it. So the summary cannot say every date here is a live plan, and
        // the watch-out names both fallbacks (Codex, #545 again).
        //
        // ⚠️ And the chain only runs when the phases hold an END somewhere:
        // listPhaseSpans returns null without one, and the row drops to the
        // job's current_start_date, which is planned-only — so a baseline with
        // no end anywhere shows blank, not the baseline (Codex, #545).
        //
        // ⚠️ ↺ IS ANY MOBILIZATION, NOT A REMOBILIZATION. latest_mob_start is the
        // highest-sequence mobilization's start, and mob #1 qualifies, so the
        // swap and the mark fire on a job's FIRST mobilization too — effectiveStart
        // and remobOverride only ask whether one exists. "After a remobilization"
        // taught readers ↺ means a second mob (Codex, #545). But only when the
        // LATEST mob has a start_date: the query is TOP 1 by mob_sequence, so a
        // dateless latest mob yields null and no ↺ even if an earlier one had a
        // date. And "shows that date", not "replaces the start": a fab row keeps
        // its phase lines and adds a separate "↺ Job" line (ScheduleCell), while
        // the sort and filter use the mob start on both (Codex, #545).
        //
        // And the watch-out is read by anyone (this entry is ungated), so it says
        // who sets a Planned Start rather than telling a read-only reader to do
        // it — Edit Schedule only renders with projects.edit (Codex, #545).
        id: 'mod-projects-planned-start',
        title: 'The Planned Start column',
        summary:
          '**Planned Start** shows the start each job is working to — the Planned Start on its **Schedule & Dates** card — and it is what the sort and the start-date filter use, with two exceptions. When the job\'s latest mobilization has a start date, the list shows that date marked **↺** and sorts and filters by it — a first mobilization counts, not only a remobilization — and on a fabrication job a phase can fall back to another date, below.',
        watchOut: [
          'On a fabrication job with an end date on some phase, a phase with no **Planned Start** shows its **Original Start** — the baseline, not a live plan — or failing that its **Actual Start**. With no end date anywhere, the row shows the job\'s planned start or nothing. Once the phase has a Planned Start the list uses it — whoever edits the job\'s schedule, normally the PM, sets it in **Edit Schedule**.',
          'For when work actually began, look on the job\'s **Manpower** tab.',
        ],
        deepLink: '/projects',
      },
    ],
  },
  {
    id: 'mod-overview',
    name: 'Overview',
    location: 'Project workspace ▸ Overview',
    whatItsFor:
      'The front page of a job — who is on it, the key dates, the setup checklist, and the activity feed of who did what.',
    topics: [
      {
        id: 'mod-overview-health',
        title: 'The health badge is calculated, never typed',
        summary:
          'On Track / At Risk / Critical is derived from the job\'s dates and status by a fixed set of rules, first match wins. Nobody sets it by hand, so it cannot be "corrected" — you change the facts underneath and it follows.',
        watchOut: [
          'The badge shows the reason it reached that verdict. If the reason looks wrong, the underlying date or status is wrong.',
        ],
      },
      {
        id: 'mod-overview-activity',
        title: 'The activity feed',
        summary:
          'A per-job record of who changed what, newest first, across every part of the job. Cost-bearing entries are hidden from anyone without financial access, so two people can see different feeds on the same job.',
      },
    ],
  },
  {
    id: 'mod-weekly-report',
    name: 'Weekly Report',
    location: 'Project workspace ▸ Weekly Report',
    whatItsFor:
      'The weekly project report for one job, one week at a time: what the Tracker fills in, ready to export to Word for the internal copy and the client copy, and, once the finished report is uploaded, the report itself, readable here without opening Word.',
    topics: [
      {
        id: 'mod-weekly-what',
        title: 'Pick the week the report goes out',
        summary:
          'Click a week on the strip of weeks at the top to see its report. It covers the daily logs and photos from the week before, and looks ahead three weeks starting with the week you picked.',
        watchOut: [
          'Draft daily logs show on the tab but are **not exported**. Submit a log first if it belongs in the report.',
          'A yellow **!** on a week means its finalized report hasn’t been uploaded yet.',
        ],
      },
      {
        id: 'mod-weekly-after',
        title: 'Read the finalized report: Before upload and After upload',
        summary:
          'Right above the report, **Before upload** shows the Tracker’s draft for the week, the way the export starts it. **After upload** shows the finalized internal report as it was uploaded: every section, line and table of the Word file, with the week’s site photos where the file had them. A week whose report is uploaded opens on **After upload**.',
        watchOut: [
          'Photos pasted into Word by hand don’t show here; the site photos from the daily logs do.',
          'A report uploaded before the Tracker read whole reports shows no text until someone who can edit the job clicks **Read the full report from the file**.',
        ],
      },
      {
        id: 'mod-weekly-export',
        title: 'Export to Word, then finish the report there',
        summary:
          '**Export internal** and **Export client** each download a Word file named for the week. The Tracker’s own figures always come from the Tracker: the job’s details and assignments, the headcounts, the equipment and the week’s daily logs. Everything you typed in the last finalized report you uploaded starts this one: the customer team, project details and phone numbers the Tracker doesn’t have, the look-ahead, Materials, Survey, Safety and Logistics with any tables, Manpower notes and rows you added (and a headcount you typed for a week with nothing on the crew schedule yet), equipment you added, anything you typed under a table (Project Details, the teams, Manpower), and any section you added. **Finish it in Word**, and review the whole file before sending it.',
        watchOut: [
          'What you type in Word reaches the Tracker only when you upload the finished file: it shows under **After upload** for that week, and starts next week’s report.',
          'The progress summary is new every week, from that week’s daily logs.',
          'The equipment lines the Tracker writes are rebuilt from the job every week, so anything typed into one is replaced. Put your own equipment notes on a line of their own.',
        ],
        // The buttons show only to people who can edit the job.
        access: { anyPermission: ['projects.edit'] },
      },
      {
        id: 'mod-weekly-client',
        title: 'The client copy includes only the days you tick',
        summary:
          '**Export client** opens a window listing the week’s daily logs. **None are ticked**: tick each day whose log and photos the client should see. Manpower and equipment never go in the client copy.',
        access: { anyPermission: ['projects.edit'] },
      },
      {
        id: 'mod-weekly-upload',
        title: 'Upload the finalized reports, one week at a time',
        summary:
          'Once a week’s reports are sent, **upload the finished Word files** to that week: **Upload internal** every week, and **Upload client** in weeks a client copy went out. They go to the job’s SharePoint folder, under “Weekly Reports”. The internal report then shows under **After upload** for that week, and what you typed in each one starts **next week’s** export.',
        watchOut: [
          'Roles the job fills (estimator, project managers, project engineer, superintendent, safety) always come from the job, never from last week’s file: assign them in the Tracker, not in Word. A phone or email you type for the job’s own person carries only while the Tracker has none for them.',
          'If the Word file is edited in SharePoint after it was uploaded, click **Read the file again** under **After upload**, so the Tracker shows the change and carries it into next week.',
          'Weeks go **in order**: a week can’t be uploaded until the week before it has its internal report, or is marked **No report** (for a demobilized or shutdown week). The job’s first one sets where its reports start, so start with this week or last week.',
          'The client copy carries forward only into the next client copy, never from the internal one.',
        ],
        access: { anyPermission: ['projects.edit'] },
      },
    ],
  },
  {
    id: 'mod-contacts',
    name: 'Contacts',
    location: 'Project workspace ▸ Contacts',
    whatItsFor:
      'Who is on the job: our team, and the owner, design team, subs and suppliers, from the address book every job shares.',
    topics: [
      {
        id: 'mod-contacts-who',
        title: 'Our team, then everyone else by their role on the job',
        summary:
          'The top card is our team: the project managers, engineer, estimator and safety manager set on the job’s Overview, and its field leaders, with a phone number where the field directory has one. Below it are the outside contacts, grouped by their role on this job. Click an email to write to someone, or a phone number to call it from a phone.',
        watchOut: [
          'Our team is changed on the job’s Overview, not here.',
        ],
      },
      {
        id: 'mod-contacts-add',
        title: 'Adding someone: the address book first',
        summary:
          '**Add Contact**: pick their role on this job, then pick them from the address book, which every job shares. For a subcontractor or supplier, the job’s subcontractors from Intacct are listed first. If they aren’t in the book, add them as **Someone new**: at a company already in the book, at a new company (with the trades it works in, by CSI division), or at none.',
        watchOut: [
          'The book refuses a second company with the same name, or a second person with the same email, and says which entry to pick instead.',
          '**Edit** changes their role and note on this job, and their details in the book — including moving them to another company, one in the book or a new one. A new email, phone or company shows on **every job** they’re on.',
          '**Remove** takes them off this job only. They stay in the address book.',
          'If someone else changed something you are changing too (the person, their company, or their role or note on this job) after you opened **Edit**, your save is refused rather than undoing their change. Close it, open them again, and redo yours.',
        ],
        access: { anyPermission: ['projects.edit'] },
      },
    ],
  },
  {
    id: 'mod-daily-logs',
    name: 'Daily Logs',
    location: 'Project workspace ▸ Daily Logs',
    whatItsFor:
      'The site record: crews, hours, work performed, quantities, delays, safety notes and photos. One log per job per day.',
    topics: [
      {
        id: 'mod-logs-lifecycle',
        title: 'Draft, then submitted — and submitted is final',
        summary:
          'A log stays editable as a draft for as long as you need. Submitting saves it and then locks it permanently.',
        watchOut: [
          '**Nobody can unlock a submitted log** — not an admin. If in doubt, leave it as a draft overnight.',
        ],
      },
      {
        // #617. Reference, not procedure — the steps live in pm-daily-logs-rollup.
        // Worded WITHOUT naming the top-navigation tab in the module's location, since
        // field-leader-only readers see this module and are redirected from that tab.
        id: 'mod-logs-company-wide',
        title: 'The company-wide Daily Logs tab',
        summary:
          'A separate **Daily Logs** tab in the top navigation lists logs from every job you can see in one table — date, job, author, weather, crew size, man-hours, summary and status — with date, status and search filters. It is read-only: a log is still written and submitted inside its job.',
        watchOut: [
          'It shows the **last 30 days** until you change the dates, and **no more than 500 logs** at a time. A day with no log has no row, so it cannot be used to spot a missing day.',
        ],
        deepLink: '/daily-logs',
        access: { hideFromFieldLeaderOnly: true },
      },
    ],
  },
  {
    id: 'mod-mobilization',
    name: 'Mobilization',
    location: 'Project workspace ▸ Mobilization',
    whatItsFor:
      'Getting the job ready to start: the readiness checklist, and the mobilization phases with their dates.',
    topics: [
      {
        id: 'mod-mob-two-halves',
        title: 'The checklist and the phases are gated separately',
        summary:
          'One tab, two things, two different permissions. Project engineers can work the checklist but not the phases — which looks like a broken page until you know.',
      },
    ],
  },
  {
    id: 'mod-schedule',
    name: 'Schedule',
    location: 'Project workspace ▸ Schedule',
    whatItsFor: 'The job\'s phase dates and milestones, and where an imported schedule lands.',
    topics: [
      {
        id: 'mod-schedule-divergence',
        title: 'When the mobilization dates and the schedule disagree',
        summary:
          'The two are kept deliberately separate and the tracker flags a divergence rather than silently picking one. Accepting the flag writes the displayed set across, so check which one is right before you accept.',
      },
      {
        // #385. Reference, not procedure — the steps live in pm-start-mismatch
        // and mp-start-mismatch, one per owner. What belongs here is the
        // concept a reader needs to orient: what is being compared, that it
        // shows on both screens, and that it has no accept button on purpose.
        id: 'mod-schedule-crew-mismatch',
        title: 'When the crew schedule and the planned start disagree',
        summary:
          '**⚠ Start ≠ Crew** compares the job\'s planned start with the first day crew is booked on the crew schedule. It shows on the job\'s **Schedule & Dates** card and on the crew grid — though the grid never badges a Complete, Canceled or archived job — and unlike the mobilization flag it has no accept button — the two dates belong to different people, so one of them has to change theirs.',
      },
    ],
  },
  {
    id: 'mod-manpower',
    name: 'Manpower',
    location: 'Project workspace ▸ Manpower',
    whatItsFor:
      'Hours actually worked on the job, taken from the payroll feed — who was on site, for how long, week by week.',
    topics: [
      {
        id: 'mod-manpower-actuals',
        title: 'These are actuals, not a plan',
        // #541 added the Actual start tile. The old second sentence — "Nothing
        // on this tab is edited by hand" — had been false since the request card
        // and the projected-hours override arrived (the request topic just below
        // contradicted it), so it goes rather than being left beside the tile.
        //
        // The tile's rule, from jobActualStart + jobActualStartUnclaimed in
        // lib/schedule: the EARLIEST start recorded on any phase wins; only with
        // none recorded does the Paycor date come in, and a PM's clear on ANY
        // phase keeps it out. So a cleared phase blanks the tile only when no
        // other phase has a start — a fab job with fabrication started and
        // installation cleared shows the fabrication date. The first wording
        // said a clear leaves it blank, full stop (Codex, #545).
        //
        // ⚠️ And blank is mostly NOT a clear. A new job with no phase start and
        // no field hours yet is blank too (ManpowerTab: both sources null), so
        // the normal pre-start state leads and the clear is the exception —
        // "blank only when a PM cleared it" read a manual override into every
        // job that simply has not started (Codex, #545 again).
        summary:
          'The figures here are hours already worked, imported from Paycor. **Actual start** is the earliest actual start recorded on any of the job\'s phases or, failing that, the first day of field hours in Paycor — so it is blank until the job starts; and once a PM has cleared an actual start on any phase to record that work has not begun, Paycor hours no longer fill it, and only a start entered on a phase does.',
        watchOut: [
          'The tab also carries a planned-vs-actual comparison. Both sides stop at the payroll feed\'s cut-off — otherwise the current week always reads as a shortfall that is not real.',
          'A weekly crew figure is the week\'s **peak day**, never a sum of the days.',
        ],
      },
      {
        id: 'mod-manpower-request',
        title: 'The request card',
        summary:
          'At the top of the tab, **+ Manpower Request** (or **+ Forecast Update** on a SYSTAC bid) sends the schedulers a crew request for this job. It shows only to the job\'s primary PM — the bid\'s estimator on a SYSTAC row — to anyone holding the manpower request permission, and to admins.',
        watchOut: [
          'A request never writes the crew schedule. A scheduler decides it in **Manpower ▸ Requests** and books approved crew by hand; the decision and note come back on the request and on the job\'s Overview feed.',
        ],
        access: { hideFromFieldLeaderOnly: true },
      },
      {
        // manpower.edit only, like the card itself (product owner, 2026-09-22:
        // the only role responsible for travel acknowledgments). A PM on this
        // tab sees no card, so the appendix must not describe one to them.
        id: 'mod-manpower-travel-ack',
        title: 'The Travel Acknowledgments card',
        summary:
          'Schedulers issue Travel Acknowledgment links from this card and see who has signed. It shows only to the schedulers who issue them, and to admins.',
        watchOut: [
          'The Project Tracker does not send the link — the scheduler copies it and sends it by text or email. The Crew Scheduling track\'s travel section walks through the whole process.',
        ],
        access: { anyPermission: ['manpower.edit'] },
      },
      {
        // Company-wide Manpower is behind the field-leader guard in App.tsx, so
        // a field leader must not be pointed at it. Same reason this module's
        // location line no longer mentions the top navigation.
        id: 'mod-manpower-companywide',
        title: 'The company-wide crew schedule',
        summary:
          'Top navigation ▸ Manpower is a different screen with the same name: the PLANNED crew schedule looking forward, across every job. Different data, different owner, and it is where crews are assigned rather than reported.',
        watchOut: [
          'It opens on **Self-Perform**, which is every division **except** Fabrication — fab crews are shop labor and are not available for self-perform work, so counting them overstates the crew a scheduler can deploy. **All Divisions** and **Fabrication** are one click away on the same control.',
          'Fabrication jobs are **left off the grid entirely** in that view — not shown with an empty row. So a fab job you were expecting is missing rather than looking unscheduled, which is a scope question, not a crew one. Check the scope buttons before chasing anybody.',
          'If fabrication is the only part of the schedule you can edit, the page opens on **Fabrication** instead — Self-Perform would hand you a grid with none of your jobs on it.',
          'Because of that, a link to the schedule does not always carry the scope: switch away from your usual view and it does, stay on your usual view and it does not. So a link opens on **your** default, which need not be the one the sender was looking at. Say which scope you mean when you send one.',
        ],
        access: { hideFromFieldLeaderOnly: true },
        deepLink: '/manpower',
      },
    ],
  },
  {
    id: 'mod-equipment',
    name: 'Equipment',
    location: 'Project workspace ▸ Equipment',
    whatItsFor:
      'Equipment requested for this job — what is coming, what is on site, and when it goes back.',
    topics: [
      {
        id: 'mod-equipment-owned-vs-rented',
        title: 'Owned units come from a catalog',
        summary:
          'Rented equipment is free text. Owned equipment is chosen from a maintained list of unit numbers, so the same excavator is not recorded three different ways.',
      },
      {
        id: 'mod-equipment-companywide',
        title: 'Every job’s equipment in one place',
        summary:
          'Top navigation ▸ Equipment lists the same records across every job, which is how the yard sees what is out and when it is due back.',
        access: { hideFromFieldLeaderOnly: true },
        deepLink: '/equipment',
      },
      {
        // #571. For readers outside the audience, who otherwise take the
        // missing rate for a lost one; the audience (everyone with
        // equipment.edit, plus Admin) is taught the rate itself in the
        // Operations track (ops-rental-agreement). Here, in the appendix,
        // because it is the one place every non-field-leader reader reaches.
        // The rate is only ever on the company-wide page, hence the same
        // field-leader rule as the topic above.
        //
        // Since 2026-10-12 everyone who can edit a request sees its rate and
        // files, so the earlier "ask before deleting a request whose files you
        // can't see" advice no longer has a reader and was removed.
        id: 'mod-equipment-rental-terms',
        title: 'The rental rate is for equipment editors',
        summary:
          'A request’s rental rate and its rental agreement are for the people who can edit equipment requests (equipment managers, PMs and project engineers) and administrators: on the company-wide Equipment page, anyone else sees no Rate on any request, rather than a blank one, and the rate sheet’s list prices everyone can see are not that rate, so ask one of them if you need it.',
        access: { hideFromFieldLeaderOnly: true },
      },
    ],
  },
  {
    id: 'mod-survey',
    name: 'Survey',
    location: 'Project workspace ▸ Survey',
    whatItsFor: 'Survey requests raised against a job, and the survey team\'s log of work done.',
    topics: [
      {
        id: 'mod-survey-requests',
        title: 'Requests arrive from a form',
        summary:
          'Survey requests come in from a Formstack form rather than being typed here, so a request you submitted appears without anyone re-keying it.',
      },
      {
        id: 'mod-survey-companywide',
        title: 'The survey team’s own queue',
        summary:
          'Top navigation ▸ Survey is the same requests across every job, which is how the survey team works through them.',
        access: { hideFromFieldLeaderOnly: true },
        deepLink: '/survey',
      },
    ],
  },
  {
    id: 'mod-receiving',
    name: 'Receiving',
    location: 'Project workspace ▸ Receiving',
    whatItsFor:
      'Shipping and receiving orders — what has been ordered in, who is carrying it, and whether it has landed.',
    topics: [
      {
        id: 'mod-receiving-link',
        title: 'An order does not have to belong to a job',
        summary:
          'Orders can be linked to a project or stand alone, because not everything ordered is for one job.',
      },
      {
        id: 'mod-receiving-companywide',
        title: 'All incoming orders',
        summary:
          'Top navigation ▸ Receiving shows every order, including those not tied to a job — which is the view the office works from.',
        access: { hideFromFieldLeaderOnly: true },
        deepLink: '/receiving',
      },
    ],
  },
  {
    id: 'mod-shipping',
    name: 'Shipping',
    location: 'Project workspace ▸ Shipping',
    whatItsFor:
      'Requests for Logistics to pick something up or deliver it, with what they need to know to do it.',
    topics: [
      {
        id: 'mod-shipping-details',
        title: 'A request carries what Logistics needs',
        summary:
          'The dimensions, the weight, the truck, whether the jobsite can receive it, and an onsite contact. A delivery to a job cannot be submitted without the contact, their phone number and whether the jobsite can receive it.',
      },
      {
        id: 'mod-shipping-link',
        title: 'A request does not have to belong to a job',
        summary:
          'Excess material coming back from a job and vendor pickups belong to no job, so a request can be raised without one from the Shipping page.',
      },
      {
        id: 'mod-shipping-pickup-notice',
        title: 'A Materials line moved to Pickup tells Logistics',
        summary:
          'Setting a Materials line to the Pickup stage sends Logistics a short message that a pickup is coming: the item, the job and the vendor. It is not a request and carries no dimensions, weight, truck or onsite contact. The Request pickup button on the line raises a request with those, and Logistics is told again.',
      },
      {
        id: 'mod-shipping-companywide',
        title: 'All requests',
        summary:
          'Top navigation ▸ Shipping shows every request across every job, open ones first and Expedited ones at the top. It is for people who can create Receiving orders; anyone who can see a job reads that job\'s requests on its Shipping tab. Logistics changes any request; a project manager changes only the ones on their own jobs.',
        access: { anyPermission: ['receiving.edit', 'projects.view_all'], hideFromFieldLeaderOnly: true },
        deepLink: '/shipping',
      },
    ],
  },
  {
    id: 'mod-materials',
    name: 'Materials',
    location: 'Project workspace ▸ Materials',
    whatItsFor:
      'Material commitments on the job — what has been bought, against which purchase order, and how much of it has been released.',
    topics: [
      {
        id: 'mod-materials-sage',
        title: 'Purchase orders come from Sage, nightly',
        summary:
          'PO data is read from Intacct and refreshed overnight. Nothing here is typed twice, and nothing written here flows back to Sage.',
        watchOut: [
          'Sage tracking is per purchase order, matched on the job and document number — not on the cost code.',
        ],
      },
    ],
  },
  {
    id: 'mod-documents',
    name: 'Documents',
    location: 'Project workspace ▸ Documents',
    whatItsFor:
      'Controlled documents for the job, with version history. Files live in the job\'s SharePoint folder; the tracker records which version is current.',
    topics: [
      {
        id: 'mod-documents-versions',
        title: 'Superseding, not replacing',
        summary:
          'Uploading a newer version supersedes the previous one rather than overwriting it, so the chain of what was issued when survives.',
      },
    ],
  },
  {
    // #644 slice 1. The follow-up meeting and the PDF come later; this says only what is built.
    id: 'mod-meetings',
    name: 'Meetings',
    location: 'Project workspace ▸ Meetings',
    whatItsFor:
      'The job’s meetings — OAC and the others — each with its agenda, who was invited and who came, and the minutes recorded against each item.',
    topics: [
      {
        id: 'mod-meetings-read',
        title: 'A meeting is its agenda, with the minutes on each item',
        summary:
          'The **Meetings** tab lists the job’s meetings, newest first, with when they start (in Eastern time), whether they are planned, held or cancelled, and how many of their items are still open. Open one to see who attended and, item by item, what was discussed, its status (Open, Closed or Info only), who is responsible and when it is due.',
        watchOut: [
          'Each type of meeting is numbered on its own: the job’s third OAC meeting is **OAC Meeting #3**, whatever other meetings came between. A meeting with a title of its own shows that instead.',
          'Starting a follow-up meeting from the last one, and a PDF of the minutes, are not built yet.',
        ],
      },
    ],
  },
  {
    // #665. The feed itself shipped in #560 under Daily Logs and was never
    // written up here; this is its first entry.
    id: 'mod-photos',
    name: 'Photos',
    location: 'Project workspace ▸ Photos (also Daily Logs ▸ Photos)',
    whatItsFor:
      'Every photo posted on the job, whoever took it and whether or not it went with a daily log: newest first, with who posted it, and when and where it was taken.',
    topics: [
      {
        id: 'mod-photos-one-feed',
        title: 'One feed, two ways in',
        summary:
          'The **Photos** tab (in the Documents group) and the **Photos** switch on the Daily Logs tab show the same photos. A photo posted in one shows in the other, and in that day’s daily log. The files are stored in the job’s SharePoint folder, under **Daily Log Photos**.',
        watchOut: [
          'Photos that were put straight into the SharePoint folder, without going through the Project Tracker, do not show here.',
        ],
      },
      {
        id: 'mod-photos-posting',
        title: 'Posting photos without a daily log',
        summary:
          'You don’t need a daily log to post. Anyone who can edit the job, and field leaders on their own jobs, can post from either place.',
        steps: [
          'Open the job’s **Photos** tab and click **＋ Add photos**.',
          'Pick one or more photos (up to 30 at a time). On a phone you can take one or choose from your library.',
          'Check the site day (it starts as the day the earliest photo was taken, or today if the photos don’t say), add a caption if it helps, and post.',
        ],
        watchOut: [
          'Uploads run under **your own** Microsoft account, so you need access to the job’s SharePoint folder.',
          'No **＋ Add photos** button? The job may have no SharePoint Job Folder linked yet, or its job number may start with **SYSTAC-** or **ClosedWon-**, which blocks new photos until the office finishes setting it up.',
        ],
        access: { anyPermission: ['daily_logs.edit', 'projects.edit'] },
      },
      {
        id: 'mod-photos-finding',
        title: 'Finding an old photo',
        summary:
          'The feed is grouped by month and by day. **Month** jumps straight to a month (the list shows how many photos each has). **Search** looks through captions, comments and the name of whoever posted. **Posted by**, **From** and **To** narrow it further.',
        watchOut: [
          'Search can’t see what is in a photo. A caption saying what it shows (“north wall rebar before pour”) is what makes it findable later.',
        ],
      },
    ],
  },
  {
    id: 'mod-drawings',
    name: 'Drawings',
    location: 'Project workspace ▸ Drawings',
    whatItsFor: 'Drawing sets issued to the job, and which set is the current one.',
    topics: [
      {
        id: 'mod-drawings-sets',
        title: 'Registered as sets',
        summary:
          'Drawings are tracked as issued sets rather than individual sheets, which is how they arrive and how people refer to them.',
      },
    ],
  },
  {
    id: 'mod-changes',
    name: 'Change Mgmt',
    location: 'Project workspace ▸ Change Mgmt',
    whatItsFor:
      'Potential and accepted changes on the job — PCOs, CORs and owner change orders, how far each has got with the client, and whether each accepted change has been entered in Intacct.',
    topics: [
      {
        id: 'mod-changes-stages',
        title: 'A change moves from PCO to COR to OCO',
        summary:
          'A PCO is a potential change. Marking it sent to the client makes it a COR, and the client accepting it makes it an OCO. It is one change the whole way along, and it keeps its number.',
        watchOut: [
          'Each step records who clicked and the date you give. The date the client accepted is the date the change counts on, so it should be the day they accepted.',
          'Declining or withdrawing ends the change. It cannot be reopened; open a new one.',
          'A change that has sat in its stage for a week (a PCO not yet sent, a COR with the client, or an accepted change not yet linked to Sage) is flagged: an amber line on its row, the **Needs attention** tile (click it to show only those), and a row on the Today page of each project manager on the job. Only people who can edit the job get any of these. An accepted change counts toward Revenue Won only once it is linked, so that one is the flag to act on first. The number of days is a configuration value, so it can be changed without a release.',
        ],
      },
      {
        id: 'mod-changes-intacct',
        title: 'Accepted changes are entered in Intacct by hand',
        summary:
          'The tracker does not push changes to Intacct. It records that someone has entered them, and shows a queue of accepted-but-not-entered items so none is forgotten. Linking a change to its Intacct change order also saves Intacct\'s amount as the amount that counts.',
        watchOut: [
          'The revised contract shown here is derived for display. Intacct remains the system of record.',
          'If Intacct\'s amount changes after the link, the change is flagged and a PM decides whether to take the new amount.',
        ],
      },
    ],
  },
  {
    id: 'mod-punch',
    name: 'Punch List',
    location: 'Project workspace ▸ Punch List',
    whatItsFor:
      'The job’s list of unfinished or wrong work near the end of the job: who has to fix each item, whose move it is right now, and the before and after photos.',
    topics: [
      {
        id: 'mod-punch-ball',
        title: 'Status and ball in court are two different things',
        summary:
          'Status says where an item is (Open, Ready for review, Closed). Ball in court says whose move it is — E&W, the sub, or the architect/owner — and it changes hands through the item’s buttons, never by typing.',
        watchOut: [
          'To change many items at once, tick them in the table and use the bar that appears: reassign, change the due date, or pass the ball. Any item that can’t take the change (a closed one, say) is skipped and stays ticked, with the reason.',
          'On a computer, Drawings opens the job’s drawing sets with each item’s pin on its sheet, in its status color. Drop Pin to Add Item starts an item where you click, with the room printed nearest the pin filled in (check it); an item’s own page has View on Drawing and Place on Drawing or Move Pin. A pin stays on the revision it was dropped on, even after a newer one is uploaded.',
          'An item can carry short video clips, up to 60 seconds each: + Add a video in the item, or on the last step when adding an item on a phone. A video is extra — the before and after photos are still what an item needs. The close-out report says when an item has a video in the Job Folder; the sub sheets don’t show videos.',
          'Marking an item fixed needs an after photo, and each new repair needs a new one. The after photos on an item when it is marked fixed, sent back or reopened stay with it as the record of that repair, even if the fix was rejected.',
          'Publishing moves the list from E&W Internal Punch to the Published Punch List and can’t be undone. Open items of the subs you don’t carry over close as “not carried over”.',
          'Once every item is closed and the list is closed out, Download Close-out Report prints the record: every item by number with its before and after photos, who marked it fixed, verified it and recorded the architect/owner approval, and how often it was rejected. Reopening an item takes the report away until the list is closed out again. While a list is closed out, its photos stay on the items and whether architect/owner approval is required can’t change: reopen the item first.',
        ],
        deepLinkNote: 'Open a job, then the Punch List tab.',
      },
      {
        id: 'mod-punch-lists',
        title: 'Each job sets its own trades and who can be assigned',
        summary:
          'In Punch List ▸ Settings, the PM or PE can add trades the standard list doesn’t have, hide standard ones the job doesn’t use, and add names to “Assigned to”: a sub not in Sage, one of our crews, or a person.',
        watchOut: [
          'Hiding or removing a trade only takes it out of the choices for new items. Items that already have it keep it, and you can still filter by it.',
          'A name stays in “Assigned to” while an item or a trade default still uses it, even after you remove it.',
        ],
        deepLinkNote: 'Open a job, then Punch List ▸ Settings.',
      },
      {
        id: 'mod-punch-sheets',
        title: 'Sub sheets: print what each sub has to fix',
        summary:
          'Sub Sheets (or Sheet on a sub’s row) shows each sub’s New, Open and Rejected items with the before photo, room and due date, one sheet per sub. Print them, or save them as a PDF from the print dialog.',
        watchOut: [
          'Printing doesn’t send anything by itself. After you print, mark the New items as sent: they become Open and the ball moves to the sub.',
          'Copy Email Text puts a sub’s list in a draft you paste into Outlook (the Project Tracker doesn’t have the subs’ email addresses). With All selected you get one draft per sub.',
        ],
        deepLinkNote: 'Open a job, then Punch List ▸ Sub Sheets.',
      },
      {
        id: 'mod-punch-phone',
        title: 'On a phone: walk the building room by room',
        summary:
          'On a phone the Punch List is grouped by room. Add Here starts an item in that room, and Add Item takes you through the photo, what needs fixing, the trade and where, one step at a time.',
        watchOut: [
          'Save & Next goes straight back to the camera and keeps the room and trade, so you can add a room’s items in a row.',
          'When you check a fix yourself on site, add the after photo and tap I Checked It — Verify Now: it’s marked fixed and verified in one go.',
          'In bright sun, tap the sun button for high contrast. It stays on for you on that phone until you turn it off.',
          '📍 Drawings shows the job’s drawing sets with each item’s pin; tap a pin to see its item. To pin, drag the drawing until the spot is under the red crosshair and tap Pin Here: Pin on Drawing on the last step of Add Item (it fills in the room printed nearest the pin; check it), Add Item Here on the drawing, or Move Pin on an item.',
        ],
        deepLinkNote: 'Open a job on your phone, then the Punch List tab.',
      },
    ],
  },
  {
    id: 'mod-qaqc',
    name: 'QA / QC',
    location: 'Project workspace ▸ QA / QC',
    whatItsFor:
      'Concrete ordering and quality: pour cards from the field, the approved mix list, the job\'s supplier and blanket POs, and inspection reports. The company-wide screen is the pour schedule the office works from.',
    topics: [
      {
        id: 'mod-qaqc-pour-lifecycle',
        title: 'The pour card lifecycle',
        summary:
          'A pre-pour form from the field becomes a pour card. The office confirms it with the supplier, and it is marked poured — usually automatically, from the daily log.',
        table: {
          caption: 'Pour card states',
          headers: ['State', 'Means'],
          rows: [
            ['Pending', 'Submitted by the field, not yet confirmed with the supplier'],
            ['Confirmed', 'The office has placed the order'],
            ['Poured', 'The concrete went in'],
            ['Covered', 'A will-call that came in with the main pour, so it needs no separate delivery'],
            ['Cancelled', 'Called off, or superseded by a newer form'],
          ],
        },
      },
      {
        id: 'mod-qaqc-mix-flag',
        title: 'What a mix flag means',
        summary:
          'The Mix ID from the field is checked against the mixes approved for that job. A flag says "check this", never "stop" — an order can still be confirmed and poured.',
        watchOut: [
          'An empty approved list means nothing is checked at all, and nothing is flagged. That is deliberate.',
        ],
      },
    ],
  },
  {
    id: 'mod-rfis',
    name: 'RFIs',
    location: 'Project workspace ▸ RFIs',
    whatItsFor: 'Requests for information raised on the job, numbered automatically, through to the answer.',
    topics: [
      {
        id: 'mod-rfis-disposition',
        title: 'Every answered RFI has to be dispositioned',
        summary:
          'When an RFI comes back answered, it must be marked either as producing a change or as having no cost impact. That is what stops a cost impact being noticed months later.',
      },
      {
        id: 'mod-rfis-export',
        title: 'Export to Excel gives you the company RFI form',
        summary:
          '**Export to Excel**, on any RFI, downloads the company\'s Excel RFI form with what the RFI holds already filled in. The file is named the usual way: job number, RFI number, subject and date, as in "10999 - RFI 007 - Wall Top Elevations 2026.10.19". The boxes the Project Tracker has nothing for (To, Address, Drawings, Cost Impact, Schedule Impact, Suggestion, Submitted By) stay yellow for you to fill in. Anyone who can open the RFI can export it.',
      },
    ],
  },
  {
    id: 'mod-submittals',
    name: 'Submittals',
    location: 'Project workspace ▸ Submittals',
    whatItsFor: 'Submittals issued for review, their status, each round of review, and the returned documents.',
    topics: [
      {
        id: 'mod-submittals-rounds',
        title: 'A submittal keeps every round of its review',
        summary:
          'The **Review rounds** card shows Rev 0, the original, and one more round for each Send Revision. Each round has its own dates, outcome, comments and files: the package that went out, and the reviewer\'s marked-up copy. Reviewers cannot upload, so a returned markup is saved by someone here.',
      },
      {
        id: 'mod-submittals-export',
        title: 'Export to Excel gives you the company submittal transmittal',
        summary:
          '**Export to Excel**, on any submittal, downloads the company\'s Excel submittal transmittal with what the submittal holds already filled in, for the round being sent: number and Rev, reviewer (as Attention), sent date, job, title and spec section, and the files on that round. Contents is ticked for Submittal and for the submittal\'s type. The file is named job number, submittal number, title and date, as in "10999 - Submittal 012 - Rebar Shop Drawings 2026.10.20". The boxes the Project Tracker has nothing for (To, Address, Pages, Submitted By) stay yellow, and Delivery and Response Needed stay unticked, for you to fill in. Anyone who can open the submittal can export it.',
      },
      {
        id: 'mod-submittals-mix',
        title: 'Concrete mix submittals connect to QA / QC',
        summary:
          'An approved mix design submittal can be linked from the job\'s approved mix list, tying the approval back to the paperwork that granted it.',
      },
    ],
  },
  {
    id: 'mod-safety',
    name: 'Safety',
    location: 'Project workspace ▸ Safety · and Top navigation ▸ Safety',
    whatItsFor:
      'A read-only view of safety records for the job, mirrored from the safety system. Nothing is entered here.',
    access: { hideFromFieldLeaderOnly: true },
    topics: [
      {
        id: 'mod-safety-readonly',
        title: 'Mirrored, not owned',
        summary:
          'Safety data is collected in the safety tool. The tracker shows it beside the job so you do not have to go looking, but corrections are made at the source.',
      },
    ],
  },
  {
    id: 'mod-financials',
    name: 'Financials',
    location: 'Project workspace ▸ Financials',
    whatItsFor:
      'The job\'s work-in-progress picture: contract, budget, committed, cost to date, the forecast, and the margin that falls out of them.',
    access: { anyPermission: ['financials.view'], requiresCostsEnabled: true },
    topics: [
      {
        id: 'mod-financials-vocabulary',
        title: 'The words on this tab',
        summary: 'Five terms carry most of the meaning; the rest is arithmetic on them.',
        table: {
          headers: ['Term', 'Means'],
          rows: [
            ['Budget', 'The estimate, by cost code, as synced from Sage'],
            ['Committed', 'Open purchase orders and subcontracts — money promised, not yet spent'],
            ['Actual', 'Cost posted to the general ledger against the job'],
            // ⚠️ The default described here is the same rule as the PM track's
            // `pm-financials` topic (its steps and its first watch-out), and the source of
            // truth is `defaultCtc` / `releaseRemaining` in lib/wip.ts (#605). Change all
            // three together, or the guide will disagree with itself and with the tab.
            ['Cost to complete','What the PM says is still to come. Until a cost code is forecast, the tracker assumes its remaining budget will be spent — or nothing, once Intacct marks the job Completed'],
            ['EAC', 'Actual + cost to complete: what the job is expected to cost in total'],
          ],
        },
      },
      {
        id: 'mod-financials-double-lock',
        title: 'Why you might not see this tab',
        summary:
          'Financials needs BOTH a financial permission and a company-wide setting to be switched on. Having the permission alone is not enough, which is why access can look inconsistent between people.',
      },
      {
        // Facts as in the PM track's pm-transfer-budget; this is the reference
        // half, so it says where the button lives and who can press it rather
        // than repeating the steps.
        id: 'mod-financials-budget-transfers',
        title: 'Budget transfers sit behind Update Forecast',
        summary:
          'Transfer Budget appears only while the forecast is being updated, and it needs its own permission on top of forecast access. Each transfer is written at once, recorded in the Budget Transfer Log at the foot of the tab and as a line on the job’s Overview feed for anyone who can see financials, and never deleted. It changes the budget in the Project Tracker only — Intacct, and the Job Cost Management Report, keep the estimate as synced.',
      },
    ],
  },
  {
    id: 'mod-commitments',
    name: 'Commitments',
    location: 'Project workspace ▸ Commitments',
    whatItsFor:
      'Budget against what has actually been committed and spent, the buyout plan for what is still to be bought, and the month-end accrual schedule.',
    access: { anyPermission: ['financials.view'], requiresCostsEnabled: true, hideFromFieldLeaderOnly: true },
    topics: [
      {
        id: 'mod-commitments-stale-plan',
        title: 'A planned figure can go stale',
        summary:
          'If a new purchase order lands after you recorded a buyout plan, the plan is flagged as stale rather than quietly adjusted. Re-saving it takes a fresh snapshot.',
      },
      {
        id: 'mod-commitments-accruals',
        title: 'Accruals are a schedule, not a number the rest of the tracker uses',
        summary:
          'The **Accruals** view holds the month-end accrual schedule, one saved set per job per month. It stands on its own: nothing in it feeds Actual, cost to complete, EAC or the work-in-progress figures.',
        table: {
          caption: 'How each line\'s total accrued is worked out',
          headers: ['Section', 'Total accrued is'],
          rows: [
            ['Material', 'Carry-over + newly accrued'],
            ['Equipment', 'Cost per unit × number of units (priced by the day, week or month)'],
            ['Subcontracts', 'Contract value × (percent complete − percent billed to date)'],
          ],
        },
        watchOut: [
          'A subcontract billed ahead of the work gives a **negative** accrual, flagged **Billed ahead**. It is shown as it falls, not rounded to zero, so check the two percentages.',
        ],
      },
      {
        id: 'mod-commitments-accruals-who',
        title: 'Who can enter accruals',
        summary:
          'Anyone who can open the Commitments tab can read the schedule. Entering or changing it takes the same access as editing the forecast and the buyout plan, so if the **Enter accruals** button is missing, ask an administrator.',
      },
    ],
  },
  {
    id: 'mod-project-reports',
    name: 'Reports (on a job)',
    location: 'Project workspace ▸ Reports',
    whatItsFor: 'Reporting scoped to the one job you have open.',
    access: { hideFromFieldLeaderOnly: true },
    topics: [
      {
        id: 'mod-project-reports-scope',
        title: 'Scoped to this job',
        summary:
          'Everything here is about the open job. The company-wide equivalents live under Reports in the top navigation.',
      },
      {
        id: 'mod-project-reports-granted',
        title: 'Each report is granted on its own',
        // The empty-state wording is the tab's own (ProjectReportsTab). Access is
        // reports.view, because the workspace hides the tab without it — the
        // entry's own rule stays as it was.
        summary:
          'The Reports tab opens for anyone with reports access, but every report inside it needs its own permission, so two people can open the same tab and see different reports. Someone who holds none sees “No reports shared with your role” — ask an administrator for the ones you need. A report also only opens on a job you can already see.',
        access: { anyPermission: ['reports.view'] },
      },
      {
        id: 'mod-project-reports-which',
        title: 'Which report answers which question',
        summary:
          'Both reports share a layout — cost codes, then change orders, then a grand total — and a sign: Balance is Estimated − Actual, so a negative balance is over the estimate.',
        table: {
          headers: ['Report', 'Answers', 'Figures come from'],
          rows: [
            [
              'Job Cost Management Report',
              'Is each cost code over or under its estimate, in dollars?',
              'The Intacct job estimate and general-ledger job cost, plus open POs and subcontracts when you turn them on',
            ],
            [
              'Man Hours Report',
              'Is each cost code over or under its estimated hours?',
              'Estimated labor dollars divided by the company labor rate, against Paycor timecards',
            ],
          ],
        },
        access: { anyPermission: ['reports.view'] },
      },
    ],
  },

  // ── Company-wide ──────────────────────────────────────────────────────────
  {
    id: 'mod-rollups',
    name: 'Reports and roll-ups',
    location: 'Top navigation ▸ Reports',
    whatItsFor:
      'The same information as the job tabs, but across every job you can see — schedule, manpower, WIP, safety and the rest.',
    // App.tsx redirects /reports unless the user holds reports.view (or Admin),
    // on top of the field-leader guard. Both, or the appendix advertises a
    // screen that bounces the reader back to Projects.
    access: { anyPermission: ['reports.view'], hideFromFieldLeaderOnly: true },
    topics: [
      {
        id: 'mod-rollups-scope',
        title: 'A roll-up only ever shows jobs you can already see',
        summary:
          'Roll-ups respect the same visibility as the Projects list. Two people can open the same report and get different totals, and both are correct.',
      },
    ],
  },
  {
    id: 'mod-ppr',
    name: 'Post Project Review',
    location: 'Top navigation ▸ PPR',
    whatItsFor:
      'The close-out review for a finished job — captured from a form, held against the job, and exported as a report.',
    // /ppr needs ppr.view OR ppr.manage (or Admin), and field-leader-only users
    // are redirected before that check even runs.
    access: { anyPermission: ['ppr.view', 'ppr.manage'], hideFromFieldLeaderOnly: true },
    topics: [
      {
        id: 'mod-ppr-source',
        title: 'Answers arrive from a form',
        summary:
          'Reviews are submitted through Formstack and land here automatically. The tracker is where they are read and reported on, not where they are filled in.',
      },
    ],
  },
  {
    id: 'mod-admin',
    name: 'Administration',
    location: 'Top navigation ▸ Admin',
    whatItsFor:
      'Users and roles, company settings, the lists the rest of the app draws on, and the Sage connection.',
    access: { adminOnly: true },
    topics: [
      {
        id: 'mod-admin-scope',
        title: 'One administrator',
        summary:
          'There is a single Admin. The role bypasses every permission check in the app, which is why almost nothing else is granted it.',
      },
    ],
  },
  {
    id: 'mod-training',
    name: 'Training guide',
    location: 'The ? Help button ▸ Full training guide',
    whatItsFor: 'This document. Role tracks for how to do the work, and this appendix for what each screen is.',
    topics: [
      {
        id: 'mod-training-scope',
        title: 'It shows what YOU can reach',
        summary:
          'The guide is filtered by your own access, so it does not teach you buttons you do not have. Another person\'s copy will legitimately differ from yours.',
        // No deepLink, deliberately: this topic IS the screen you are reading.
        // "Take me there →" now opens the tracker in its own tab, so a self-link
        // opened a SECOND copy of the guide — which then renounced the tracker
        // tab's name (see releaseAppTab) and left the next jump to open a third
        // tab (review, #310). The module's `location` above says where it lives.
      },
    ],
  },
];
